import { useEffect, useRef, useState } from "react";
import { ChevronDown, Cloud, Save, Users } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useDesign } from "../lib/store";
import { fetchSharedRoom } from "../lib/sharedRoomsCloud";
import type { SharedRoomDoc } from "../lib/sharedRoomTypes";
import { SaveCopySheet } from "./SaveCopySheet";

/**
 * Collapsed share status in the top bar. The full "shared by …" line
 * used to float over the canvas (and then as its own header row). Both
 * ate the Snap / Measure band or pushed every toolbar down. This stays
 * inside the existing 44px bar and unfolds a popover over the canvas
 * only while it's open.
 *
 * Hidden in private sessions. Non-owners get "Save a copy" in the
 * popover — the escape hatch for forking the design into their library.
 */
export function SharedRoomBanner() {
  const sharedRoomId = useDesign((s) => s.activeSharedRoomId);
  const design = useDesign((s) => s.design);
  const { user } = useAuth();

  const [doc, setDoc] = useState<SharedRoomDoc | null>(null);
  const [open, setOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sharedRoomId) {
      setDoc(null);
      setOpen(false);
      return;
    }
    let active = true;
    void fetchSharedRoom(sharedRoomId).then((next) => {
      if (active) setDoc(next);
    });
    return () => {
      active = false;
    };
  }, [sharedRoomId]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node | null;
      if (rootRef.current && target && rootRef.current.contains(target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!sharedRoomId) return null;

  const ownerName = doc?.ownerDisplayName ?? "the owner";
  const memberCount = doc ? Object.keys(doc.members).length : 1;
  const otherCount = Math.max(0, memberCount - 1);
  const isOwner = !!(doc && user && doc.ownerUid === user.uid);
  const baseName = doc?.name ?? design.room.name ?? "Shared room";
  const defaultCopyName = `${baseName} (copy)`;
  const sourceRoomName = doc?.name ?? design.room.name ?? "this shared room";

  return (
    <>
      <div ref={rootRef} className="relative shrink-0">
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label="Shared room"
          title="Shared room"
          data-testid="shared-room-banner"
          onClick={() => setOpen((v) => !v)}
          className={`h-11 md:h-8 max-md:w-11 max-md:justify-center md:pl-2 md:pr-1.5 inline-flex items-center gap-1 rounded-full ring-1 text-caption font-medium transition-colors ${
            open
              ? "bg-ink-900 text-white ring-ink-900"
              : "bg-white text-ink-700 ring-ink-200 hover:bg-ink-50"
          }`}
        >
          <Cloud className={`size-3.5 shrink-0 ${open ? "text-white/80" : "text-ink-400"}`} />
          <span className="hidden md:inline">Shared</span>
          <ChevronDown
            className={`hidden md:block size-3.5 shrink-0 transition-transform ${open ? "rotate-180" : ""} ${
              open ? "text-white/70" : "text-ink-400"
            }`}
          />
        </button>

        {open && (
          <div
            role="dialog"
            aria-label="Shared room"
            className="absolute left-0 top-[calc(100%+6px)] z-40 w-56 rounded-2xl bg-white text-ink-700 ring-1 ring-ink-200 shadow-xl shadow-ink-900/10 p-3"
          >
            <div className="text-caption leading-snug">
              {isOwner ? (
                <>
                  <span className="font-semibold text-ink-900">Shared by you</span>
                  {otherCount > 0 ? (
                    <span className="mt-1 flex items-center gap-1 text-ink-500">
                      <Users className="size-3 shrink-0" />
                      {otherCount} other{otherCount === 1 ? "" : "s"}
                    </span>
                  ) : (
                    <span className="mt-1 block text-ink-500">Just you so far</span>
                  )}
                </>
              ) : (
                <>
                  Shared by{" "}
                  <span className="font-semibold text-ink-900">{ownerName}</span>
                  {otherCount > 0 ? (
                    <span className="mt-1 flex items-center gap-1 text-ink-500">
                      <Users className="size-3 shrink-0" />
                      {otherCount} other{otherCount === 1 ? "" : "s"}
                    </span>
                  ) : null}
                </>
              )}
            </div>
            {!isOwner && (
              <button
                type="button"
                data-testid="shared-room-save-copy"
                onClick={() => {
                  setOpen(false);
                  setSheetOpen(true);
                }}
                className="mt-2.5 w-full h-8 px-2.5 rounded-xl bg-ink-50 ring-1 ring-ink-200 inline-flex items-center justify-center gap-1.5 text-caption font-medium text-ink-800 hover:bg-ink-100 transition-colors"
              >
                <Save className="size-3.5" />
                Save a copy…
              </button>
            )}
          </div>
        )}
      </div>

      <SaveCopySheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        defaultName={defaultCopyName}
        sourceRoomName={sourceRoomName}
      />
    </>
  );
}
